// src/components/SplashScreen.jsx
// Startbildschirm: der Schriftzug „fluent!“ schreibt sich selbst (aus design/logo/fluent-wordmark-d-script.svg),
// danach blendet der Splash aus. Er liegt als Ebene über der App, damit darunter schon geladen werden kann.
import React, { useEffect, useState } from "react";

// Alle Zeiten sind gegenüber dem Original-SVG um 20 % verkürzt.
// So lange läuft die Schreib-Animation (Welle endet bei 2,88 s + 0,64 s), plus kurze Pause
const ANIMATION_MS = 3700;
const FADE_MS = 600;

function prefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
}

/**
 * ready: true, sobald die App darunter fertig geladen ist.
 * Ausgeblendet wird erst, wenn die Animation durch ist UND ready gilt (Tippen überspringt die Animation).
 */
export default function SplashScreen({ ready, onDone }) {
  const [animationDone, setAnimationDone] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const ms = prefersReducedMotion() ? 800 : ANIMATION_MS;
    const t = setTimeout(() => setAnimationDone(true), ms);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (ready && animationDone) setFading(true);
  }, [ready, animationDone]);

  // Nach dem Ausblenden ganz entfernen
  useEffect(() => {
    if (!fading) return;
    const t = setTimeout(() => onDone && onDone(), FADE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fading]);

  return (
    <div
      className={`splash${fading ? " splash-fading" : ""}`}
      onClick={() => setAnimationDone(true)}
      role="presentation"
    >
      <FluentWordmark />
    </div>
  );
}

function FluentWordmark() {
  return (
    <svg
      className="splash-wordmark"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="6 10 340 132"
      role="img"
      aria-label="fluent!"
    >
      <style>{`
        .fw-s { fill: none; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 1 2;
                animation: fw-write var(--d) var(--t, 0s) cubic-bezier(.35, 0, .65, 1) both; }
        .fw-ink { stroke: var(--fluent-night); stroke-width: 5.5; }
        /* Welle läuft sanft aus (Ease-out) statt gleichmäßig zu bremsen */
        .fw-wave { stroke: var(--fluent-sun); stroke-width: 4;
                   animation-timing-function: cubic-bezier(.25, .6, .3, 1); }
        .fw-dot { fill: var(--fluent-sun); transform-box: fill-box; transform-origin: center;
                  animation: fw-pop .28s 2.68s cubic-bezier(.3, 1.6, .5, 1) both; }
        @keyframes fw-write {
          from { stroke-dashoffset: 1.05; opacity: 0; }
          1%   { opacity: 1; }
          to   { stroke-dashoffset: 0; opacity: 1; }
        }
        @keyframes fw-pop { from { transform: scale(0); } to { transform: scale(1); } }
        @media (prefers-reduced-motion: reduce) { .fw-s, .fw-dot { animation: none; stroke-dasharray: none; } }
      `}</style>
      {/* Kursiv: alles um 12° geneigt, Grundlinie (y = 100) bleibt an ihrem Platz */}
      <g transform="translate(21 0) skewX(-12)">
        {/* Schriftzug in einem Zug */}
        <path
          className="fw-s fw-ink"
          style={{ "--d": "1.92s" }}
          pathLength="1"
          d="M16 100 C30 92 52 66 58 40 C62 22 50 16 46 32 C42 50 43 84 43 126 C43 136 52 134 55 122 C58 110 66 102 76 98
      C90 90 102 66 104 42 C106 26 94 24 92 40 C90 58 90 86 94 96 C97 104 106 102 112 92
      C116 84 118 74 119 66 C118 82 116 100 130 100 C140 100 145 86 147 66 C146 82 143 100 155 100
      C164 100 176 92 182 84 C190 74 186 64 177 66 C165 69 163 98 182 100 C192 101 200 94 205 86
      C207 80 209 72 211 66 C211 78 211 92 211 100 C214 82 222 66 234 67 C245 68 240 92 244 99 C248 104 256 99 262 90
      C266 72 270 52 272 38 C270 58 266 86 270 97 C273 103 282 101 290 93"
        />
        {/* Querstriche von f und t */}
        <path
          className="fw-s fw-ink"
          style={{ "--d": "0.2s", "--t": "2s" }}
          pathLength="1"
          d="M30 66 Q45 62 60 64"
        />
        <path
          className="fw-s fw-ink"
          style={{ "--d": "0.2s", "--t": "2.24s" }}
          pathLength="1"
          d="M256 58 Q274 54 292 56"
        />
        {/* Ausrufezeichen: Strich */}
        <path
          className="fw-s fw-ink"
          style={{ "--d": "0.2s", "--t": "2.48s" }}
          pathLength="1"
          d="M321 32 C319 52 316 70 314 84"
        />
      </g>
      {/* Ausrufezeichen: Punkt (ungeneigt, damit er rund bleibt) */}
      <circle className="fw-dot" cx="311.5" cy="100" r="4.2" />
      {/* Welle */}
      <path
        className="fw-s fw-wave"
        style={{ "--d": "0.64s", "--t": "2.88s" }}
        pathLength="1"
        d="M70 120 q19 -9 38 0 t38 0 t38 0 t38 0 t38 0 t38 0"
      />
    </svg>
  );
}
